import React, { useState } from 'react';
import { X, MapPin, Calendar, Clock, Users, Check, ArrowRight } from 'lucide-react';
import { FixedOffer } from '../pages/FixedOffers';
import { LuxuryCar } from '../pages/LuxuryCars';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { differenceInDays } from 'date-fns';
import currency from 'currency.js';

const exchangeRates = {
  CHF: 1,
  USD: 1.1,
  EUR: 1.02,
  GBP: 0.9
};

interface OfferDetailModalProps {
  offer: FixedOffer | LuxuryCar;
  onClose: () => void;
}

export default function OfferDetailModal({ offer, onClose }: OfferDetailModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    currency: 'CHF'
  });
  const [range, setRange] = useState<DateRange | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAddons, setShowAddons] = useState(false);
  const [addons, setAddons] = useState<string[]>([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const toggleAddon = (addon) => {
    setAddons((prev) =>
      prev.includes(addon) ? prev.filter((a) => a !== addon) : [...prev, addon]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const currencyCode = formData.currency;
      const rate = exchangeRates[currencyCode] || 1;
      const days = range?.from && range.to ? differenceInDays(range.to, range.from) + 1 : 1;
      const base = offer.price * days * rate;
      const tax = base * 0.081;
      const total = base + tax;

      const emailText = `New Booking Request\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nOffer: ${offer.title}\nFrom: ${range?.from?.toDateString()} To: ${range?.to?.toDateString()}\n\nPricing:\nDuration: ${days} day(s)\nSubtotal: ${currencyCode} ${currency(base).format()}\nTaxes: ${currencyCode} ${currency(tax).format()}\nTotal incl. tax: ${currencyCode} ${currency(total).format()}\n\nAdd-ons: ${addons.join(', ') || 'None'}\n\nMessage:\n${formData.message}`;

      console.log(emailText);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Submission error', err);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto">
      <div className="min-h-screen pt-0 pb-10 px-0 md:px-6">
        <div className="w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/80 rounded-full text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </button>

          <div className="relative w-full h-72 md:h-[400px]">
            <img
              src={offer.image_url || 'https://source.unsplash.com/random/1600x600?jet'}
              alt={offer.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="px-4 sm:px-6 md:px-12 pt-10 pb-16 max-w-5xl mx-auto">
            <h1 className="text-3xl font-bold mb-4">{offer.title}</h1>
            <p className="text-gray-600 whitespace-pre-line mb-6">{offer.description}</p>

            {('origin' in offer) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700 mb-6">
                <div className="flex items-center"><MapPin className="mr-2" /> {offer.origin} → {offer.destination}</div>
                <div className="flex items-center"><Calendar className="mr-2" /> {offer.departure_date}</div>
                <div className="flex items-center"><Clock className="mr-2" /> {offer.duration}</div>
                <div className="flex items-center"><Users className="mr-2" /> Up to {offer.passengers}</div>
              </div>
            )}
            {('location' in offer) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700 mb-6">
                <div className="flex items-center"><MapPin className="mr-2" /> {offer.location}</div>
                <div className="flex items-center"><Calendar className="mr-2" /> {offer.year}</div>
                <div className="flex items-center"><Clock className="mr-2" /> {offer.type}</div>
                <div className="flex items-center"><Users className="mr-2" /> {offer.brand}</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700 mb-6">
              <div className="flex items-center"><Calendar className="mr-2" /> {offer.departure_date}</div>
              <div className="flex items-center"><Clock className="mr-2" /> {offer.duration}</div>
              <div className="flex items-center"><Users className="mr-2" /> Up to {offer.passengers}</div>
            </div>

            <div className="mb-4">
              <label className="block font-medium mb-1">Preferred Currency <span className="ml-1">-</span></label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="CHF">CHF</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block font-medium mb-1">Select Date Range</label>
              <DayPicker
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={2}
                pagedNavigation
                modifiersClassNames={{
                  selected: 'bg-black text-white',
                  range_middle: 'bg-gray-200 text-gray-800',
                  range_start: 'bg-black text-white',
                  range_end: 'bg-black text-white',
                }}
              />
            </div>

            <div className="mb-4">
              <label className="block font-medium mb-1 flex items-center justify-between">
                Select Additional Services
                <button
                  type="button"
                  onClick={() => setShowAddons(!showAddons)}
                  className="ml-2 text-xl text-black"
                >
                  {showAddons ? '-' : '+'}
                </button>
              </label>
              {showAddons && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {['Concierge Service', 'Airport Pickup', 'Champagne On Board', 'Extra Baggage'].map((addon) => (
                    <label key={addon} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={addons.includes(addon)}
                        onChange={() => toggleAddon(addon)}
                      />
                      <span>{addon}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {offer.price && (
              <div className="mb-6 border border-gray-200 p-4 rounded-lg bg-gray-50">
                {(() => {
                  const currencyCode = formData.currency;
                  const rate = exchangeRates[currencyCode] || 1;
                  const days = range?.from && range.to ? differenceInDays(range.to, range.from) + 1 : 1;
                  const base = offer.price * days * rate;
                  const tax = base * 0.081;
                  const total = base + tax;
                  return (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Base Price</span>
                        <span className="font-semibold">{currencyCode} {currency(base / days).format()}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Duration</span>
                        <span className="font-medium">{days} day{days > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Subtotal</span>
                        <span>{currencyCode} {currency(base).format()}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Taxes (8.1%)</span>
                        <span>{currencyCode} {currency(tax).format()}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold mt-2 border-t pt-2">
                        <span>Total incl. tax</span>
                        <span>{currencyCode} {currency(total).format()}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input name="name" value={formData.name} onChange={handleChange} required placeholder="Full Name" className="w-full border px-3 py-2 rounded" />
                <input name="email" value={formData.email} onChange={handleChange} required placeholder="Email Address" type="email" className="w-full border px-3 py-2 rounded" />
                <PhoneInput country={'ch'} value={formData.phone} onChange={handlePhoneChange} inputClass="w-full" containerClass="w-full" inputStyle={{ width: '100%' }} />
                <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Additional Requests" className="w-full border px-3 py-2 rounded resize-none" />
                <button disabled={isSubmitting} type="submit" className="w-full bg-black text-white py-3 rounded hover:bg-gray-800">
                  {isSubmitting ? 'Sending…' : 'Request Booking'}
                </button>
              </form>
            ) : (
              <div className="bg-green-100 text-green-800 p-4 rounded mt-4">Your booking request was sent successfully!</div>
            )}

            <div className="mt-6 text-center text-sm text-gray-500">
              Questions? Email <a href="mailto:bookings@privatecharterx.com" className="underline">bookings@privatecharterx.com</a> or call <a href="tel:+41447978853" className="underline">+41 (0) 44 797 88 53</a><br />
              Languages: Deutsch, English, Français, Italiano, Español
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}